import React from 'react';
import EbookShell from '../components/EbookShell';

const RVTravelGuideEbook = () => (
  <EbookShell ebookType="rv-travel-guide" variant="travel" testIdPrefix="travel-guide">
    {/* Cover */}
    <section className="page cover" data-testid="travel-guide-cover">
      <div className="ai-glow" />
      <div className="brand">RV Match A.I.</div>
      <h1>The Adventure Outdoors RV Travel Guide</h1>
      <p className="subhead">Find your perfect RV. Explore without limits. Travel smarter with AI-powered confidence.</p>
      <div className="cta">RVMatchAI.app</div>
      <div className="mountains" />
      <div className="road" />
      <div className="footer"><span>Adventure Guide</span><span>Powered by RV Match A.I.</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter One</div>
      <h2>Plan the Trip Before You Pick the RV</h2>
      <p>Most buyers start with floorplans. Smart buyers start with lifestyle.</p>
      <div className="card-grid">
        <div className="card"><h3>Budget</h3><p>Know your payment comfort zone, fuel costs, campsite costs, and ownership expenses.</p></div>
        <div className="card"><h3>Tow Vehicle</h3><p>Your truck or SUV determines what you should safely consider.</p></div>
        <div className="card"><h3>Travel Style</h3><p>Weekend camping, national parks, full-time travel, or family road trips all require different RVs.</p></div>
        <div className="card"><h3>Must-Haves</h3><p>Bunks, outdoor kitchen, storage, washer/dryer prep, office space, or pet-friendly layout.</p></div>
      </div>
      <div className="footer"><span>01</span><span>Plan smarter. Travel better.</span></div>
    </section>

    <section className="page">
      <div className="brand">RV Match A.I.</div>
      <h2>Stop Guessing. Start Matching.</h2>
      <div className="image-box">Smart RV matching for real-life adventures</div>
      <p>RV Match A.I. helps buyers compare lifestyle, budget, towing needs, and comfort preferences so they can find the right RV faster.</p>
      <p>This turns confusion into clarity — and turns shopping into confidence.</p>
      <div className="footer"><span>02</span><span>RVMatchAI.app</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Two</div>
      <h2>Destinations That Change You</h2>
      <p>Some trips are vacations. Others become memories you talk about for the rest of your life.</p>
      <div className="card-grid">
        <div className="card"><h3>Yellowstone</h3><p>Wildlife, geysers, wide-open scenery, and unforgettable campgrounds.</p></div>
        <div className="card"><h3>Zion</h3><p>Red rock canyons, dramatic hikes, and iconic RV-friendly routes.</p></div>
        <div className="card"><h3>Acadia</h3><p>Ocean views, coastal air, and classic New England RV adventure.</p></div>
        <div className="card"><h3>Moab</h3><p>Desert sunsets, off-road trails, and world-class outdoor adventure.</p></div>
      </div>
      <div className="footer"><span>03</span><span>Adventure Outdoors</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Three</div>
      <h2>Life on the Road</h2>
      <div className="image-box">Coffee. Campfires. Open roads.</div>
      <ul>
        <li>Keep driving days around 150&ndash;250 miles.</li>
        <li>Leave space in the schedule for unexpected discoveries.</li>
        <li>Use cellular backup, campground Wi-Fi, or Starlink when needed.</li>
        <li>Bring familiar items for pets and kids.</li>
        <li>Cook simple meals that make the RV feel like home.</li>
      </ul>
      <div className="footer"><span>04</span><span>Freedom with structure</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Four</div>
      <h2>Travel Smart. Stay Safe.</h2>
      <div className="card-grid">
        <div className="card"><h3>Before You Leave</h3><p>Check tires, lights, hitch, mirrors, fluids, and your RV height.</p></div>
        <div className="card"><h3>On the Road</h3><p>Drive slower, leave space, take wider turns, and rest often.</p></div>
        <div className="card"><h3>Weather</h3><p>Respect wind, storms, heat, and cold. Never push through unsafe conditions.</p></div>
        <div className="card"><h3>Emergency Kit</h3><p>First aid, jumper pack, tools, flashlight, tire kit, and roadside membership.</p></div>
      </div>
      <div className="footer"><span>05</span><span>Confidence beats speed</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Five</div>
      <h2>You Won&rsquo;t Remember the Miles. You&rsquo;ll Remember the Moments.</h2>
      <div className="image-box">Sunset memories beside the RV</div>
      <p>Take the picture. Make the coffee. Light the fire. Let the kids plan a day. Try the local food. Talk to the people at the campground.</p>
      <p>The RV is not the destination. It is the doorway.</p>
      <div className="footer"><span>06</span><span>Make it legendary</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Six</div>
      <h2>Boondocking &amp; Off-Grid Power</h2>
      <div className="card-grid">
        <div className="card"><h3>Battery Bank</h3><p>Lithium (LiFePO4) is worth every penny. 200Ah runs a fridge + fan + lights for 3 nights easily.</p></div>
        <div className="card"><h3>Solar Sizing</h3><p>400W panels replenish typical daily use in a sunny climate. Double it for cloudy PNW or shaded sites.</p></div>
        <div className="card"><h3>Water Discipline</h3><p>2 gal / person / day drinking + cooking. Navy shower knocks bathing use to 3 gal. 40-gal tank = ~4 days for two.</p></div>
        <div className="card"><h3>Grey / Black Tank Life</h3><p>Filter dish water into a jug. Toss on the ground. Extends grey by 60&ndash;70%. Only pull the black plug at dump stations.</p></div>
        <div className="card"><h3>Propane Reality</h3><p>Fridge on propane sips 1&ndash;2 lb / day. Furnace burns propane AND battery (fan). Wear a hoodie first.</p></div>
        <div className="card"><h3>Where to Boondock Legally</h3><p>BLM land, National Forest dispersed sites, Harvest Hosts, Boondockers Welcome. NEVER on private land without permission.</p></div>
      </div>
      <div className="footer"><span>07</span><span>The best sites are free ones</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Seven</div>
      <h2>Weather, Seasons &amp; Regional Playbook</h2>
      <ul>
        <li><strong>Spring &mdash; South &amp; Desert.</strong> Arizona, Utah, New Mexico. Cool nights, warm days, wildflowers. Book Zion &amp; Moab 3+ months out.</li>
        <li><strong>Early Summer &mdash; Pacific Northwest &amp; Northern Rockies.</strong> Olympic, Glacier, Yellowstone. Wildflowers, waterfalls, snowmelt rivers.</li>
        <li><strong>Late Summer &mdash; Coastal &amp; High Elevation.</strong> Oregon coast, Colorado, Wyoming, Michigan UP. Escapes the inland heat dome.</li>
        <li><strong>Fall &mdash; Northeast &amp; Appalachia.</strong> Vermont, New Hampshire, Blue Ridge Parkway. Peak color mid-Sep to late-Oct.</li>
        <li><strong>Winter &mdash; Deep South &amp; Southwest.</strong> Florida, Texas Gulf Coast, Quartzsite AZ. Snowbird capital &mdash; book 6+ months out for Jan/Feb.</li>
        <li><strong>Shoulder Windows.</strong> Late Oct in Utah, early May in Yellowstone, mid-Sep in Glacier &mdash; empty parks, mild weather, best light.</li>
      </ul>
      <div className="footer"><span>08</span><span>Follow 65&deg;F &mdash; the RVer&rsquo;s compass</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Eight</div>
      <h2>The Travel-Day Routine That Prevents Disasters</h2>
      <div className="card-grid">
        <div className="card"><h3>Night Before</h3><p>Route planned + fuel stops flagged. Tank levels checked. Batteries confirmed at 100%. Sleep by 10.</p></div>
        <div className="card"><h3>Morning Coffee First</h3><p>Do NOT start breaking down until you&rsquo;re caffeinated. Every horror story starts with a rushed departure.</p></div>
        <div className="card"><h3>The Walk-Around</h3><p>Antenna down, awning locked, steps up, chocks in, hitch pinned, breakaway cable clipped. Physically touch each item.</p></div>
        <div className="card"><h3>The Interior Sweep</h3><p>Fridge latched, cabinets closed, TV secured, water pump OFF, LP OFF (or on per your rig). Toilet flushed.</p></div>
        <div className="card"><h3>Tire &amp; Weight Check</h3><p>Cold PSI on all six. TPMS paired. Weight distribution centered. Sway bar engaged.</p></div>
        <div className="card"><h3>The Roll Test</h3><p>Pull forward 20 ft. Stop. Look back. Anything left behind? Then and only then, hit the road.</p></div>
      </div>
      <div className="footer"><span>09</span><span>Slow down &mdash; save your rig</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Nine</div>
      <h2>Travel-Longer-for-Less Playbook</h2>
      <ul>
        <li><strong>Weekly &amp; monthly rates.</strong> Most parks discount 10&ndash;25% for weekly stays, 40&ndash;50% for monthly. Ask before you book nightly.</li>
        <li><strong>Passport America.</strong> $44/yr. Half-off nightly rates at 1,300+ parks. Pays for itself in one weekend.</li>
        <li><strong>Harvest Hosts.</strong> $99/yr. Free overnights at wineries, breweries, farms, museums. Support the host &mdash; buy something.</li>
        <li><strong>Thousand Trails.</strong> Regional zones from $600&ndash;$1,200/yr. Free camping if you match a zone to your travel pattern.</li>
        <li><strong>Boondockers Welcome / Hipcamp.</strong> Private-land camping for $0&ndash;$40. Amazing for one-night highway breaks.</li>
        <li><strong>Off-season the popular parks.</strong> Zion in November. Glacier in late September. Same views, 30% cost, 80% fewer people.</li>
        <li><strong>Move less often.</strong> Two nights minimum. Five-plus for real parks. Fuel + campground turnover is where money leaks fastest.</li>
      </ul>
      <div className="footer"><span>10</span><span>Roots grow when the wheels stop</span></div>
    </section>

    <section className="page cover">
      <div className="ai-glow" />
      <div className="brand">RV Match A.I.</div>
      <h1>Find the RV That Fits Your Life</h1>
      <p className="subhead">Adventure starts with the right match.</p>
      <div className="cta">Start at RVMatchAI.app</div>
      <div className="mountains" />
      <div className="footer"><span>Final Page</span><span>RVMatchAI.app</span></div>
    </section>
  </EbookShell>
);

export default RVTravelGuideEbook;
